import {engineContext} from './context.js';

(function(global){'use strict';
const VERSION='0.3.0-alpha';

function arr(x){ return Array.isArray(x)?x:[]; }

function pickChange(x){
  const candidates=[
    ...(arr(x.compoundDelta).map(v=>({type:'COMPOUND',priority:v.priority||0,text:v.summary||v.text||null}))),
    ...(arr(x.invalidations).map(v=>({type:'INVALIDATION',priority:100+(v.severity||0),text:v.summary||v.reason||v.action||null}))),
    ...(arr(x.thesisDrift).map(v=>({type:'THESIS_DRIFT',priority:v.priority||0,text:v.summary||v.text||null}))),
    ...(arr(x.meaningfulChanges).map(v=>({type:'CHANGE',priority:v.priority||0,text:v.summary||v.text||null})))
  ].filter(v=>v.text);
  candidates.sort((a,b)=>b.priority-a.priority);
  return candidates[0]||null;
}

function build(input){
  input=input||{};
  const integrity=input.integrityGate||{};
  const change=pickChange(input);

  const whatChanged = change ? change.text : 'No material research-state change detected.';
  let whyItMatters='No new decision-relevant transmission identified.';
  if(arr(input.causeTransmission).length){
    whyItMatters=input.causeTransmission[0].summary||input.causeTransmission[0].text||whyItMatters;
  } else if(arr(input.invalidations).length){
    whyItMatters='One or more prior conclusions require review because their supporting evidence changed.';
  } else if(arr(input.openGaps).length){
    whyItMatters='Open evidence gaps still limit confidence in the current research state.';
  }

  let companyTransmission='No company-specific transmission identified.';
  if(arr(input.companyTransmission).length){
    companyTransmission=input.companyTransmission[0].summary||input.companyTransmission[0].text||companyTransmission;
  }

  let nextAction='No immediate research action.';
  if(integrity.gateState==='BLOCK'){
    nextAction='Repair the blocking research-integrity issue before further decision work.';
  } else if(arr(input.reopenTasks).length){
    nextAction=input.reopenTasks[0].nextAction||`Reopen research task ${input.reopenTasks[0].taskId||''}`.trim();
  } else if(arr(input.openGaps).length){
    nextAction=input.openGaps[0].nextAction||input.openGaps[0].summary||'Close the highest-priority evidence gap.';
  } else if(input.researchQueue && arr(input.researchQueue.tasks).length){
    nextAction=input.researchQueue.tasks[0].nextAction||input.researchQueue.tasks[0].task||'Review highest-priority research task.';
  }

  const readiness = integrity.readiness || input.readiness || 'REVIEW';
  const attention = integrity.gateState==='BLOCK' ? 'BLOCKED'
    : arr(input.invalidations).length ? 'HIGH'
    : change ? 'REVIEW'
    : 'NORMAL';

  return Object.freeze({
    version:VERSION,
    symbol:input.symbol||null,
    scope:input.scope||'RESEARCH',
    whatChanged,
    whyItMatters,
    companyTransmission,
    nextAction,
    readiness,
    attention,
    integrityState:integrity.gateState||null,
    evidenceFingerprint:input.evidenceFingerprint||null,
    thesisFingerprint:input.thesisFingerprint||null,
    tradeSignal:false,
    capitalPermission:false,
    universalScore:null
  });
}

function selfTest(){
  const t=[],ok=(n,p)=>t.push({name:n,pass:!!p});
  const base={
    symbol:'AAA',scope:'OWNED',
    integrityGate:{gateState:'PASS',readiness:'READY_FOR_DECISION'},
    invalidations:[],compoundDelta:[],thesisDrift:[],meaningfulChanges:[],
    causeTransmission:[],companyTransmission:[],reopenTasks:[],openGaps:[]
  };

  const b0=build(base);
  ok('build preserves symbol',b0.symbol==='AAA');
  ok('build preserves scope',b0.scope==='OWNED');
  ok('no change gets explicit message',b0.whatChanged.includes('No material'));
  ok('healthy readiness preserved',b0.readiness==='READY_FOR_DECISION');
  ok('no change defaults normal attention',b0.attention==='NORMAL');

  const b1=build({...base,invalidations:[{action:'INVALIDATE',reason:'Revenue thesis source withdrawn',severity:2}]});
  ok('invalidation becomes top change',b1.whatChanged==='Revenue thesis source withdrawn');
  ok('invalidation raises attention',b1.attention==='HIGH');
  ok('invalidation gets why-it-matters explanation',b1.whyItMatters.includes('prior conclusions'));

  const b2=build({...base,integrityGate:{gateState:'BLOCK',readiness:'BLOCKED'}});
  ok('block integrity forces blocked attention',b2.attention==='BLOCKED');
  ok('block integrity drives repair action',b2.nextAction.includes('Repair'));

  const b3=build({...base,reopenTasks:[{taskId:'T1',nextAction:'Re-check margin evidence'}]});
  ok('reopen task drives next action',b3.nextAction==='Re-check margin evidence');

  const b4=build({...base,openGaps:[{nextAction:'Verify debt covenant terms'}]});
  ok('open gap drives next action',b4.nextAction==='Verify debt covenant terms');

  const b5=build({...base,companyTransmission:[{summary:'Higher rates pressure refinancing economics'}]});
  ok('company transmission included',b5.companyTransmission.includes('refinancing'));

  const b6=build({...base,causeTransmission:[{summary:'Demand weakness reduces utilization'}]});
  ok('cause transmission included',b6.whyItMatters.includes('utilization'));

  const b7=build({...base,compoundDelta:[{priority:4,summary:'Two independent demand signals deteriorated'}]});
  ok('compound delta can drive change line',b7.whatChanged.includes('Two independent'));

  ok('no trade signal',b7.tradeSignal===false);
  ok('no capital permission',b7.capitalPermission===false);
  ok('no universal score',b7.universalScore===null);

  return Object.freeze({passed:t.filter(x=>x.pass).length,total:t.length,tests:Object.freeze(t)});
}

const API={VERSION,pickChange,build,selfTest};
try{API.selfTestResult=selfTest()}catch(e){API.bootstrapError=String(e&&e.stack||e)}
global.ZooInvestDailyDecisionBriefV3IntegrationEngine=Object.freeze(API);
})(engineContext);